import React from 'react';

import IterativeSolversAnimation from '../Animations/IterativeLinearSolversAnimation';
import SimplexAnimation from '../Animations/SimplexAnimation';
import IconPurple from '../../Icon/IconPurple.png';

const animationFrameClasses =
  'overflow-hidden rounded-3xl border border-white/30 bg-white/10 p-4 backdrop-blur-sm shadow-lg';

const iconFrameClasses = `${animationFrameClasses} p-3 md:p-4`;

const entries = [
  {
    id: 'simplex-method',
    title: 'New: Simplex Method',
    date: '2026-01-25',
    excerpt:
      'The Simplex method is now available in NumSolve. Walk through pivots, basis changes, and optimal corners with a guided, animated workflow.',
    content: (
      <div className="space-y-4 text-base leading-relaxed text-slate-700">
        <div className="mb-6 w-full max-w-[330px] mx-auto md:float-right md:ml-8 md:mb-4 md:max-w-[330px] md:mx-0 lg:max-w-[330px]">
          <div className={animationFrameClasses}>
            <SimplexAnimation isActive size={300} className="w-full" />
          </div>
        </div>
          <p>
            We’ve added the <strong>Simplex</strong> method to NumSolve — the classic algorithm for solving linear programs.
            This solver moves from corner to corner of the feasible region until it reaches the optimum.
          </p>

          <p>
            Step through the pivots one by one, watch which variables enter and leave the basis, and follow how the tableau
            changes at each iteration. This makes it much easier to understand what simplex is actually doing under the hood.
          </p>

          <p>
            It’s useful both for learning linear programming and for solving real LPs. You can inspect every step,
            check intermediate tableaus, and review the full iteration history afterwards.
          </p>

          <p>
            Open the <strong>Simplex Algorithm</strong> workspace and try it out.
          </p>
        <div className="hidden md:block clear-both" aria-hidden="true" />
      </div>
    ),
  },
  {
    id: 'iterative-linear-solvers',
    title: 'New: Iterative Linear Solvers',
    date: '2025-11-01',
    excerpt:
      'NumSolve introduces a complete workspace for iterative linear solvers — from Richardson and Jacobi to SSOR, Conjugate Gradient, and GMRES. Explore convergence plots, preconditioning, and step-by-step logs.',
    content: (
      <div className="space-y-4 text-base leading-relaxed text-slate-700">
        <div className="mb-6 w-full max-w-[330px] mx-auto md:float-right md:ml-8 md:mb-4 md:max-w-[330px] md:mx-0 lg:max-w-[330px]">
          <div className={animationFrameClasses}>
            <IterativeSolversAnimation isActive size={300} className="w-full" />
          </div>
        </div>
        <p>
          We are excited to announce the release of the new <strong>Iterative Linear Solvers</strong> workspace in NumSolve.
          This module covers both stationary methods (Richardson, Jacobi, Gauss–Seidel, SSOR) and Krylov subspace methods
          (Conjugate Gradient and GMRES), designed for large and sparse systems where direct factorization is inefficient.
        </p>

        <p>
          You can switch between different iterative methods, set damping or relaxation parameters, and explore the impact
          of tolerance and iteration limits. The integrated explanation panel introduces the mathematical theory behind each
          algorithm.
        </p>

        <p>
          Whether you are studying numerical linear algebra or testing solvers on your own systems, this workspace makes every
          iteration transparent. As always, iteration logs are saved for reproducibility and export.
        </p>

        <p>
          Try it now in the <strong>Iterative Solvers</strong> section and visualize how convergence accelerates with
          preconditioning.
        </p>
        <div className="hidden md:block clear-both" aria-hidden="true" />
      </div>
    ),
  },
  {
    id: 'launch',
    title: 'Launch',
    date: '2025-10-03',
    excerpt:
      'Today we are opening up NumSolve to the public. Discover the first set of numerical solvers and share your feedback.',
    content: (
      <div className="space-y-4 text-base leading-relaxed text-slate-700">
        <div className="mb-6 w-full max-w-[340px] mx-auto md:float-right md:ml-8 md:mb-4 md:max-w-[340px] md:mx-0 lg:max-w-[340px]">
          <div className={`${iconFrameClasses} flex items-center justify-center`}>
            <img
              src={IconPurple}
              alt="NumSolve icon"
              className="w-56 max-w-full rounded-3xl drop-shadow-[0_35px_65px_-30px_rgba(109,40,217,0.55)] md:w-72"
            />
          </div>
        </div>
        <p>
          After months of prototyping, validating and polishing algorithms, the very first version of NumSolve is now live. The
          launch collection contains workspaces for Newton's method, Gaussian elimination, interpolation, polynomial fitting,
          matrix decompositions and numerical quadrature.
        </p>
        <p>
          Each workspace combines a hands-on solver, animations and explanations to make every step transparent. We are already
          working on additional features.
        </p>
        <p>
          Thank you for being among the first explorers. Let us know which methods you would like to see next or how the UI can
          better support your workflows.
        </p>
        <div className="hidden md:block clear-both" aria-hidden="true" />
      </div>
    ),
  },
  //  Neue Änderungen für den release: 
  // 1. Es gibt nun die SVD Methode
  // 2. Bei QR Zerlegung gibt es einen shift
  // {
  //   id: 'roadmap-sneak-peek',
  //   title: 'Roadmap Sneak Peek',
  //   date: '2025-02-22',
  //   content: (
  //     <>
  //       <p>
  //         With the launch behind us we are focusing on the next chapter: extending NumSolve with the methods you request most.
  //         Our short-term roadmap includes dedicated pages for eigenvalue solvers, non-linear optimisation routines and
  //         interactive tutorials that show how each iteration evolves.
  //       </p>
  //       <p>
  //         We are also polishing collaboration tools so you can share workspaces with teammates and annotate interesting
  //         iterations. If you would like early access, drop us a note via the contact form—beta invitations are going out soon.
  //       </p>
  //     </>
  //   ),
  // },
];

const formatDate = (iso) => new Date(iso).toLocaleDateString('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

export default function BlogPage() {
  return (
    <section className="relative mx-auto w-full max-w-5xl px-6 pb-24 pt-28 text-white lg:px-16">
      <div className="pointer-events-none absolute inset-x-0 top-10 -z-10 h-64 rounded-full bg-gradient-to-r from-[#8b5cf6]/25 via-transparent to-[#ec4899]/25 blur-3xl" />
      <header className="mb-12 space-y-4">
        <p className="text-sm uppercase tracking-[0.35em] text-white/60">NumSolve updates</p>
        <h1 className="text-4xl font-semibold leading-tight md:text-5xl">Blog</h1>
        <p className="max-w-3xl text-lg text-white/70">
          Stories about building NumSolve, shipping new numerical methods and teaching resources for curious problem solvers.
        </p>
      </header>
      <div className="space-y-8">
        {entries.map((entry) => (
          <article
            key={entry.id}
            className="rounded-3xl border border-white/10 bg-white/90 p-8 text-slate-900 shadow-[0_35px_65px_-45px_rgba(76,29,149,0.8)] backdrop-blur"
          >
            <header className="mb-4 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-fuchsia-500/70">{formatDate(entry.date)}</p>
              <h2 className="text-3xl font-semibold text-slate-900">{entry.title}</h2>
            </header>
            <div className="space-y-4 text-base leading-relaxed text-slate-700">{entry.content}</div>
          </article>
        ))}
      </div>
    </section>
  );
}
